<template>
  <div id="mealPlans">
    <vue-loading :active="isLoading" :is-full-page="true" />

    <CheckoutContainer v-if="reservation" :title="$t('subTitle.yourOrder')">
      <!-- Iterate through products -->
      <div v-for="product in reservation.items" :key="product.kkProdId">
        <div :id="product.kkProdId" class="meal-item" @click="viewProductDetails(product)">
          <img v-if="product.image1" :src="product.image1" :alt="product.name" class="meal-image" />
          <div class="d-flex flex-column gap10 flex-grow-1 justify-content-between">
            <div class="meal-details">
              <div class="meal-description">
                <div class="product-info">
                </div>
                <span>
                      {{ product.longDesc }}<template v-if="product.optionText"> ({{ cleanedProdOptionText(product.optionText) }})</template>
                    </span>
                <div class="properties"></div>
                <div class="allergens">
                  <div v-for="property in product.properties" :key="property.id">
                    <div>
                      <img :src="property.image" :alt="property.name" class="property-img" />
                    </div>
                  </div>
                  <div v-for="allergen in product.allergens" :key="allergen.id">
                    <div>
                      <img :src="allergen.image" :alt="allergen.name" class="allergen-img" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div id="price" class="d-flex flex-row gap10 w-100 flex-wrap align-items-center">
              <div
                class="button"
                @click.stop="viewProductDetails(product)"
                role="button"
                tabindex="0"
                aria-label="View product details"
              >
                <font-awesome-icon :icon="['fas', 'circle-info']" />
              </div>
              <span class="meal-quantity">{{ product.quantity }}x</span>
              <span class="meal-price">{{ product.formattedPrice }}</span>
              <span class="meal-item-total">{{ utils.formatCurrency(product.price * product.quantity, config.ACCOUNT_BALANCE_CURRENCY) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Total price -->
      <h3 class="basket-total normalText mb-0" :class="{'no-vat': !showVat}">
        <span>{{ $t('text.sum') }}:</span>
        <span class="total-price">{{ reservation.formattedTotal }}</span>
      </h3>

      <!-- VAT summary (if available) -->
      <template v-if="reservation.transVats && reservation.transVats.vatCodes && reservation.transVats.vatCodes.length">
        <div class="basket-vats w-100">
          <div
            v-for="(vat, index) in reservation.transVats.vatCodes"
            :key="'vat-' + index"
            class="vat-row"
          >
            <span>{{ $t('text.vat') }} {{ vat.formattedVatPercent }}</span>
            <span>{{ vat.formattedVatAmount }}</span>
            <span>{{ vat.formattedNetAmount }}</span>
          </div>
        </div>
      </template>

      <!-- Payments summary (if available) -->
      <template v-if="reservation.paymentsList && reservation.paymentsList.length">
        <div class="basket-payments">
          <div
            v-for="(payment, index) in reservation.paymentsList"
            :key="'payment-' + index"
            class="payment-row"
          >
            <span>{{ $t('text.paymentType') }} {{ payment.paymentType }}</span>
            <span>-{{ payment.formattedPaymentAmount }}</span>
          </div>
        </div>
      </template>

      <Button v-if="reservation.status === OrderStatus.ORDERED || reservation.status === OrderStatus.READY_TO_BE_PREPARED"
              icon-left="ban"
              class="cancel-order-button"
              @click.stop="cancelOrder(reservation.guid)"
      >
        {{ $t('button.cancelOrder') }}
      </Button>
    </CheckoutContainer>

    <!-- Modal for product details -->
    <b-modal id="product-details-modal" hide-footer :title="selectedProduct ? selectedProduct.name : ''" centered>
      <ProductDetails
        v-if="selectedProduct"
        :productId="selectedProduct.kkProdId"
        :can-order="false"
        @close="closeProductDetailsModal"
      />
    </b-modal>
  </div>
</template>

<script>
import ProductDetails from "@/components/sub-components/Product/ProductDetails.vue";
import {orderService} from "@/services/api/orderService";
import Utils from "../../../utils/utils";
import VueLoading from "vue-loading-overlay";
import {useShopStore} from "../../../stores/shop";
import {OrderStatus} from "@/constants/constants";
import productDetailsMixin from "@/components/mixins/productDetailsMixin";
import {cleanedProdOptionText} from "../../../utils/productUtils";
import CheckoutContainer from "@/components/sub-components/Container/CheckoutContainer.vue";
import Button from "@/components/barrier-free-template-components/Button.vue";

export default {
  name: "ReservationList",
  mixins: [productDetailsMixin],
  computed: {
    OrderStatus() {
      return OrderStatus
    },
    config() {
      return window.config
    },
    utils() {
      return Utils
    },
  },
  components: {
    Button,
    CheckoutContainer,
    VueLoading,
    ProductDetails,
  },
  data() {
    return {
      isLoading: false,
      selectedProduct: null,
    }
  },
  props: {
    reservation: {
      type: Object,
      required: true
    }
  },
  setup() {
    const shopStore = useShopStore();

    return {
      shopStore
    };
  },
  methods: {
    cleanedProdOptionText,
    viewProductDetails(product) {
      this.selectedProduct = product;
      this.$bvModal.show("product-details-modal");
    },
    async cancelOrder(guid) {
      try {
        this.isLoading = true;  // Set loading state
        // Call the cancel order service
        const result = await orderService.cancelOrder(this.shopStore.wondId, guid, false);
        if (result.exception) {
          // Display error if cancelation fails
          this.makeToast("danger", this.$t("notification.cancelOrderError"));
        } else {
          this.$emit('reservation-canceled');
        }
      } catch (error) {
        // Handle any unexpected errors
        this.makeToast("danger", this.$t("notification.cancelOrderError"));
      } finally {
        this.isLoading = false;  // Reset loading state
      }
    },
  }
};
</script>

<style scoped lang="scss">
@import "@/css/customizer";


.meal-container {
  background-color: var(--page-container-background);
  border-radius: var(--button-border-radius);
  margin-top: 10px
}

.meal-group {
  background: custom-chain(mealplan-group-color-reservation,mealplan-group-color,var(--page-group-header));
  text-align: left;
  margin: 0 auto;
  padding: 0 10px;
  border-radius: var(--button-border-radius);
}

.meal-group-title {
  font-size: 20px;
  color: custom-chain(mealplan-group-color-text-reservation,mealplan-group-color-text,var(--page-group-title));
  display: flex;
  justify-content: space-between; /* Space between the two spans */
  align-items: center; /* Center the text vertically */
  width: 100%; /* Ensure it takes the full width */
}

.meal-item {
  display: flex;
  flex: 1 0 360px;
  gap: 10px;
  text-align: left;
  align-items: stretch;
  padding: 10px;
  border-radius: var(--button-border-radius);
  background: custom-chain(mealplan-group-background-reservation,mealplan-group-background,var(--page-group-item));
  transition: background-color 0.3s ease, transform 0.3s ease; /* Smooth transition effect */
  height: 100%;
}

.meal-item:hover {
  background-color: #e0e0e0; /* Change background on hover */
  transform: scale(1.02); /* Slightly scale up the item */
  cursor: pointer; /* Add pointer cursor */
}

.meal-image {
  width: 120px;
  height: auto;
  margin-right: unset;
  border: custom-chain(mealplan-productimage-border,'');
  border-radius: custom-chain(mealplan-productimage-border-radius,images-border-radius,var(--button-border-radius));
  object-fit: cover;
}

.meal-details {
  display: flex;
  justify-content: space-between;
  width: 100%;
}

.meal-description {
  font-weight: bold;
  text-align: left;
}

.properties {
  filter: hue-rotate(45deg);
}

.allergens, .properties {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  margin-top: 5px;
}

.allergen-img,
.property-img {
  max-width: 30px;
}

.meal-price {
  color: indianred;
  font-size: 14px;
  align-self: center;
  flex-grow: 1;
  text-align: right;
}

.button {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  user-select: none; /* Prevent text selection */
  transition: background-color 0.3s; /* Smooth background change */
}

.button:hover,
.button:focus {
  background-color: #0056b3; /* Darker blue on hover/focus */
}

.button:active {
  background-color: #004494; /* Even darker on click */
}

.meal-item-total {
  font-weight: bold;
  color: custom-chain(mealplan-reservation-item-total,darkgreen);
  margin-left: auto;
}

.meal-quantity {
  font-size: 14px;
  color: custom-chain(mealplan-reservation-item-quantity,#555);
  margin-right: 10px;
}

#order-total {
  font-weight: bold;
  font-size: 18px;
  padding: 10px;
}

.total-amount {
  color: custom-chain(mealplan-reservation-total,darkred);
  font-size: 18px;
}

.basket-total {
  display: flex;
  justify-content: space-between;
  font-size: 20px;
  font-weight: bold;
  padding: 12px;
  border-top: 1px solid #ccc;
  margin-top: 12px;
}

.basket-total.no-vat {
  border-top: none;
  margin-top: 0;
}

.total-price {
  color: #0057b8;
}

.basket-payments, .basket-vats {
  margin-top: 10px;
  padding: 10px;
  border-top: 1px solid #ddd;
}

.payment-row, .vat-row {
  display: flex;
  justify-content: space-between;
  padding: 5px 0;
}

.cancel-order-button {
    background-color: red;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: var(--button-border-radius);
    cursor: pointer;
}
</style>
